import { useCallback, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Spinner } from '@/components'
import { FormField } from '@/components/FormField'
import { useMoistureEquilibriumContext } from '@/context/moistureEquilibrium'
import { useOilContent } from '@/hooks/useOilContent'
import { getTaxonNameDisplay } from '@/util'

export type MoistureEquilibriumFormData = {
  temp: string
  oilContent: string
  relativeHumidity: string
}

export const MoistureEquilibriumForm = () => {
  const oilContentData = useOilContent()
  const [useOilPreset, setUseOilPreset] = useState<boolean>()
  const [reference, setReference] = useState<string>()
  const { calculateMoistureEquilibrium, setCalculatingMoistureEquilibrium } =
    useMoistureEquilibriumContext()

  const { register, setValue, handleSubmit } =
    useForm<MoistureEquilibriumFormData>()

  const oilContentMap = useMemo(
    () =>
      Object.fromEntries(
        oilContentData ? oilContentData.map((oc) => [oc.id, oc]) : [],
      ),
    [oilContentData],
  )

  const onSelectOilContentPreset = useCallback(
    (id: string) => {
      const value = oilContentMap[id]
      if (value?.oil_content) {
        setValue('oilContent', value.oil_content.toString())
        setReference(value.reference.description)
      }
    },
    [oilContentMap, setValue],
  )

  const onSubmit = useCallback(
    ({ oilContent, temp, relativeHumidity }: MoistureEquilibriumFormData) => {
      calculateMoistureEquilibrium({
        oilContent: parseFloat(oilContent),
        temp: parseFloat(temp),
        relativeHumidity: parseFloat(relativeHumidity),
      })
      setCalculatingMoistureEquilibrium(false)
    },
    [calculateMoistureEquilibrium, setCalculatingMoistureEquilibrium],
  )

  return (
    <form className="flex-grow">
      <div className="form-group mb-2">
        <div className="mb-2 flex justify-between">
          <label htmlFor="input" className="text-1xl  text-gray-800">
            Oil Content
          </label>
          <button
            className="cursor-pointer rounded border-2 border-serblue-light bg-white px-0.5 text-xs"
            onClick={(e) => {
              e.preventDefault()
              setUseOilPreset(!useOilPreset)
            }}
          >
            {useOilPreset && 'Enter value'}
            {!useOilPreset && 'Use preset'}
          </button>
        </div>
        {oilContentData && useOilPreset && (
          <select
            onChange={(ev) => {
              onSelectOilContentPreset(ev.target.value)
            }}
            className="block w-full rounded-md border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-serblue-light focus:ring-serblue-light"
          >
            <option value={undefined}>Select an option</option>
            {oilContentData.map((oc) => (
              <option key={oc.id} value={oc.id}>
                {getTaxonNameDisplay(oc.species)} - {oc.oil_content}{' '}
                {oc.reference.citation}
              </option>
            ))}
          </select>
        )}
        {!oilContentData && useOilPreset && <Spinner />}
        {!useOilPreset && (
          <input
            type="text"
            autoComplete="false"
            className="block w-full rounded-md border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-serblue-light focus:ring-serblue-light"
            {...register('oilContent')}
          />
        )}
        {reference && (
          <div className="mt-1 rounded-md border border-gray-300 bg-gray-200 p-2 text-xs">
            Reference: {reference}
          </div>
        )}
      </div>
      <FormField
        label="Drying Temperature °C"
        registerProps={register('temp', { required: true })}
      />
      <FormField
        label="Relative humidity (%) of air in drying environment"
        registerProps={register('relativeHumidity', { required: true })}
      />
      <div className="flex w-full justify-end">
        <button
          type="submit"
          className="rounded border-2 border-serblue-light bg-white py-2 px-2"
          onClick={handleSubmit(onSubmit)}
        >
          Calculate
        </button>
      </div>
    </form>
  )
}
